import { ModalManager } from '@mui/material/Modal';

/* Keeps MUI's dialogs, menus and selects working when <body> holds an element
 * this page is not allowed to touch.
 *
 * WHAT IT IS FOR. Every MUI overlay — Dialog, Menu, Select, Drawer, Popover —
 * registers with one ModalManager, whose `add` and `remove` walk EVERY child of
 * the container (document.body), reading `getAttribute` / `tagName` and setting
 * `aria-hidden`. A browser extension can put its own element on <body> that page
 * scripts may not touch: in Firefox, Bitwarden's inline-autofill element throws
 * "Permission denied to access property …" on any read. `add` then throws after
 * recording the modal but before recording its container, the error escapes a
 * React effect, nothing catches it, and React unmounts the whole app — a white
 * page the moment a dropdown or dialog opens. The later `remove` throws again on
 * the missing record ("containerInfo is undefined").
 *
 * WHY IT LIVES HERE. The fault is a library walking nodes an extension owns, and
 * neither is ours to change; the page still has to work for whoever has that
 * extension installed.
 *
 * IT ONLY EVER ACTS AFTER MUI ITSELF HAS THROWN. The original method always runs
 * first and its result is returned untouched. Only if it throws does the
 * fallback finish the job the way MUI would, with every per-element step in its
 * own try/catch, so an element that cannot be touched is simply skipped.
 *
 * IT MIRRORS @mui/material 5.18.0, `Modal/ModalManager.js` — the bookkeeping of
 * `add` / `remove`, `getHiddenSiblings`, `ariaHiddenSiblings` and
 * `isAriaHiddenForbiddenOnElement`. Re-read that file on any MUI upgrade.
 */

type ManagedModal = { mount: Element; modalRef?: Element | null };

type ContainerRecord = {
  modals: ManagedModal[];
  container: HTMLElement;
  restore: (() => void) | null;
  hiddenSiblings: Element[];
};

/* The two fields MUI declares private, kept here exactly as MUI keeps them. */
type ManagerState = { modals: ManagedModal[]; containers: ContainerRecord[] };

const INSTALLED = '__incryptModalManagerGuard';

type GuardedPrototype = {
  add(this: ManagerState, modal: ManagedModal, container: HTMLElement): number;
  remove(this: ManagerState, modal: ManagedModal, ariaHiddenState?: boolean): number;
  [INSTALLED]?: boolean;
};

/* `isAriaHiddenForbiddenOnElement`: children of <body> that must never carry
 * aria-hidden, per the ARIA-in-HTML rules MUI follows. */
const FORBIDDEN_TAGS = [
  'TEMPLATE',
  'SCRIPT',
  'STYLE',
  'LINK',
  'MAP',
  'META',
  'NOSCRIPT',
  'PICTURE',
  'COL',
  'COLGROUP',
  'PARAM',
  'SLOT',
  'SOURCE',
  'TRACK'
];

const ariaHiddenForbidden = (element: Element) =>
  FORBIDDEN_TAGS.indexOf(element.tagName) !== -1 ||
  (element.tagName === 'INPUT' && element.getAttribute('type') === 'hidden');

/* Each child on its own: one this page may not touch is skipped, not fatal. */
const eachChild = (container: Element, step: (element: Element) => void) => {
  Array.prototype.forEach.call(container.children, (element: Element) => {
    try {
      step(element);
    } catch {
      /* An element this page is not allowed to read or change. */
    }
  });
};

let warned = false;

const warnOnce = () => {
  if (warned) return;
  warned = true;
  console.warn(
    'A dialog or menu could not hide or reveal an element on this page that the page is not allowed to touch (usually one a browser extension adds). That element was skipped and the dialog or menu works as normal.'
  );
};

/* The children that carry aria-hidden already — MUI's `getHiddenSiblings`, read
 * BEFORE its `add` runs. Read afterwards, a child MUI had just hidden before it
 * threw would pass for one hidden all along, and would then stay hidden for
 * good: with <div id="root"> that is the whole app lost to a screen reader. */
const alreadyHidden = (container: Element) => {
  const hidden: Element[] = [];
  eachChild(container, (element) => {
    if (element.getAttribute('aria-hidden') === 'true') hidden.push(element);
  });
  return hidden;
};

/* `add`, finished after MUI's threw — after it recorded the modal and revealed
 * its root, before it recorded the container. Whether it had hidden nothing yet
 * (it throws reading the first untouchable child) or some of the children (one
 * that can be read but not written), the pass below is safe to run again. */
const finishAdd = (
  state: ManagerState,
  modal: ManagedModal,
  container: HTMLElement,
  hiddenSiblings: Element[]
) => {
  if (state.modals.indexOf(modal) === -1) state.modals.push(modal);

  const exclude = [modal.mount, modal.modalRef, ...hiddenSiblings];
  eachChild(container, (element) => {
    if (exclude.indexOf(element) === -1 && !ariaHiddenForbidden(element)) {
      element.setAttribute('aria-hidden', 'true');
    }
  });

  const record = state.containers.find((item) => item.container === container);
  if (record) {
    if (record.modals.indexOf(modal) === -1) record.modals.push(modal);
  } else {
    state.containers.push({
      modals: [modal],
      container,
      restore: null,
      hiddenSiblings
    });
  }
  return state.modals.indexOf(modal);
};

/* `remove`, finished after MUI's threw, which it does in one of two places.
 * Un-hiding the children: by then the modal is out of both lists and `restore()`
 * has run, but its container's record is left behind with no modals. Or at the
 * start, for a modal registered with no container record at all. */
const finishRemove = (
  state: ManagerState,
  modal: ManagedModal,
  index: number
) => {
  const listed = state.modals.indexOf(modal);
  if (
    listed !== -1 &&
    !state.containers.some((item) => item.modals.indexOf(modal) !== -1)
  ) {
    state.modals.splice(listed, 1);
    return index;
  }

  for (let i = state.containers.length - 1; i >= 0; i -= 1) {
    const record = state.containers[i];
    if (record.modals.length === 0) {
      const exclude = [modal.mount, modal.modalRef, ...record.hiddenSiblings];
      eachChild(record.container, (element) => {
        if (exclude.indexOf(element) === -1 && !ariaHiddenForbidden(element)) {
          element.removeAttribute('aria-hidden');
        }
      });
      state.containers.splice(i, 1);
    }
  }
  return index;
};

/** Wraps `ModalManager.prototype.add` / `.remove` once; later calls do nothing. */
export const installModalManagerGuard = () => {
  const proto = ModalManager.prototype as unknown as GuardedPrototype;
  if (proto[INSTALLED]) return;
  proto[INSTALLED] = true;

  const originalAdd = proto.add;
  const originalRemove = proto.remove;

  proto.add = function add(modal, container) {
    // read-only, so the normal path is unchanged; only the fallback uses it
    const hiddenBefore = alreadyHidden(container);
    try {
      return originalAdd.call(this, modal, container);
    } catch {
      warnOnce();
      return finishAdd(this, modal, container, hiddenBefore);
    }
  };

  proto.remove = function remove(modal, ariaHiddenState) {
    const index = this.modals.indexOf(modal);
    try {
      return originalRemove.call(this, modal, ariaHiddenState);
    } catch {
      warnOnce();
      return finishRemove(this, modal, index);
    }
  };
};
